import React, { useState, useEffect, useRef } from "react";
import SearchIcon from "@mui/icons-material/Search";
import * as settings from "../settings";
import * as functions from "../functions";

/**
 * FloatingNavbar
 *
 * Primary navbar  ➜  logo  |  search  |  main links
 * Secondary navbar ➜  filter chips & “Save search” button
 *
 * The <header> wrapper is `fixed-top`; its total height (primary + secondary
 * rows) is measured with a ref and reported upward via `setNavbarHeight` so
 * the calling page can insert an equivalent spacer and avoid jumpy layout.
 */
const FloatingNavbar = ({ navbarHeight, setNavbarHeight }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navbarRef = useRef(null);

  const handleToggle = () => setIsOpen((prev) => !prev);
  const handleClose = () => setIsOpen(false);

  // Measure full header height (both navbars)
  useEffect(() => {
    if (!navbarRef.current) return;
    const updateHeight = () => setNavbarHeight(navbarRef.current.offsetHeight);
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [setNavbarHeight]);

  return (
    <>
      {/* ============================  FIXED HEADER  ============================ */}
      <header ref={navbarRef} className="fixed-top bg-white shadow-sm">
        {/* -----------------------  PRIMARY NAVBAR  ----------------------- */}
        <nav className="navbar navbar-expand-lg navbar-light bg-light">
          <div className="container-fluid">
            {/* Logo */}
            <a className="navbar-brand d-flex align-items-center" href="/" onClick={handleClose}>
              <img src="/logo.svg" alt="Logo" style={{ height: "45px" }} className="me-2" />
            </a>

            {/* Search (optional)
            <form
              className="d-flex flex-grow-1 mx-3"
              style={{ maxWidth: "600px", borderRadius: "1rem" }}
            >
              <input
                className="form-control px-3"
                type="search"
                placeholder="Address, City, Zip, School, Agent"
                aria-label="Search"
              />
              <button
                type="submit"
                className="btn btn-outline-secondary ms-2 d-flex align-items-center justify-content-center"
              >
                <SearchIcon />
              </button>
            </form>
            */}

            {/* Hamburger menu */}
            <button
              className="navbar-toggler"
              type="button"
              aria-controls="navbarNav"
              aria-expanded={isOpen}
              aria-label="Toggle navigation"
              onClick={handleToggle}
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            {/* Nav links */}
            <div className={`collapse navbar-collapse ${isOpen ? "show" : ""}`} id="navbarNav">
              <ul className="navbar-nav ms-auto text-end mt-2 mt-lg-0">
                <li className="nav-item">
                  <a className="nav-link" href="/" onClick={handleClose}>Home</a>
                </li>
                <li className="nav-item">
                  {/* Absolute hash paths so they work under any subroute */}
                  <a className="nav-link" href="/agents" onClick={handleClose}>Our Agents</a>
                </li>

                <li className="nav-item">
                  <a className="nav-link" href="/#about" onClick={handleClose}>About Us</a>
                </li>
                                <li className="nav-item">
                  {/* Absolute hash paths so they work under any subroute */}
                  <a className="nav-link" href="/step/100" onClick={handleClose}>Buy</a>
                </li>
                <li className="nav-item">
                  {/* Absolute hash paths so they work under any subroute */}
                  <a className="nav-link" href="/step/150" onClick={handleClose}>Sell</a>
                </li>
                <li className="nav-item">
                  <a
                    className="nav-link"
                    href="https://portal.pembre.com"
                    target="_blank"
                    onClick={handleClose}
                  >
                    Portal Login
                  </a>
                </li>

              </ul>
            </div>

            {/* Phone number (visible on desktop) */}
            <div className="d-none d-lg-block ms-3">
              <a
                href={`tel:${settings.COMPANYTELPHONE}`}
                className="navbar-text text-nowrap"
                onClick={() => functions.triggerPhoneConversion()}
              >
                {settings.COMPANYFRIENDLYPHONE}
              </a>
            </div>
          </div>
        </nav>
      </header>

      {/* Spacer to offset the fixed header height */}
      <div style={{ height: `${navbarHeight}px` }} />
    </>
  );
};

export default FloatingNavbar;